import { AutocompleteChips } from '@talentmesh/ui/components/autocomplete-chips';
import { Button } from '@talentmesh/ui/components/button';
import { Switch } from '@talentmesh/ui/components/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@talentmesh/ui/components/tooltip';
import { Info } from 'lucide-react';
import { useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import type { JobAdFormValues } from '../JobAdStep.schema';
import { getAutocompleteSkillsType, getSkillsSwitchName } from '../JobAdStepUtils';
import { useAutocompleteOptionsClient } from '@/Hooks/ClientHooks';
import JobAdSkillsType from '@/Models/JobAdSkillsType';

interface SkillsSelectorProps {
    type: JobAdSkillsType;
    label: string;
    placeholder: string;
    tooltip: string;
}

function SkillsSelector({ type, label, placeholder, tooltip }: SkillsSelectorProps) {
    const form = useFormContext<JobAdFormValues>();
    const { watch, control } = form;
    const { isSubmitting, errors, defaultValues } = form.formState;
    const name = useMemo(() => getAutocompleteSkillsType(type), []);
    const switchName = useMemo(() => getSkillsSwitchName(type), []);
    const isSkillsEnabled = watch(switchName);
    const autocompleteOptionsClient = useAutocompleteOptionsClient();

    const getAutocompleteOptions = async (search: string) => {
        return search === '' ? [] : await autocompleteOptionsClient.getAutocompleteOptions(search, type);
    };

    return (
        <div className="flex flex-col gap-2.5 px-2.5 py-5" data-cy={name}>
            <div className="flex flex-row justify-between">
                <div className="flex flex-row items-center">
                    <span className="text-foreground text-sm">{label}</span>
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button variant="ghost" size="icon-fit" type="button">
                                    <Info className="text-muted-foreground size-3.5 cursor-pointer" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent
                                sideOffset={8}
                                className="bg-card text-foreground min-w-90 p-4 text-sm drop-shadow"
                                hideArrow
                            >
                                {tooltip}
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                </div>
                <Controller
                    name={switchName}
                    control={control}
                    defaultValue={defaultValues?.[switchName] as boolean}
                    render={({ field }) => (
                        <Switch
                            checked={field.value as boolean}
                            onCheckedChange={field.onChange}
                            disabled={isSubmitting}
                            data-cy={switchName}
                        />
                    )}
                />
            </div>
            <AutocompleteChips
                name={name}
                placeholder={placeholder}
                getAutocompleteOptions={getAutocompleteOptions}
                disabled={isSubmitting || !isSkillsEnabled}
            />
            {errors[name]?.message && <p className="text-destructive text-sm">{errors[name]?.message}</p>}
        </div>
    );
}

export default SkillsSelector;
